<template>
  <v-theme-provider theme="localInsightLightTheme">
    <div class="ma-0 pa-0">
      <v-dialog
        v-model="viewReportDialog"
        fullscreen
        hide-overlay
        persistent
        scrollable
      >
        <v-card v-if="viewReportDialog">
          <v-toolbar
            :style="
              'background-color: ' +
              $store.state.config.siteConfig.toolbar_colour
            "
          >
            <v-btn
              icon
              dark
              id="closeReportViewerIcon"
              @click="
                reportDownloading
                  ? backgroundReportGeneration()
                  : (viewReportDialog = false)
              "
              aria-label="Close"
            >
              <v-icon>mdi-close</v-icon>
            </v-btn>
            <v-toolbar-title v-if="isReportManager">{{
              isReportManager
            }}</v-toolbar-title>
            <v-toolbar-title v-else>Report</v-toolbar-title>
            <v-spacer></v-spacer>
          </v-toolbar>
          <v-card-text class="ma-0 pa-0" style="height: 95vh">
            <!-- loading UX -->
            <v-card
              v-if="reportLoadingDialog"
              tile
              elevation="0"
              class="progress"
            >
              <v-card-actions class="justify-center">
                <v-progress-circular
                  v-if="reportLoadingProgress == 0"
                  :size="210"
                  :width="2"
                  color="#51627C"
                  indeterminate
                >
                  Retrieving Report
                </v-progress-circular>
                <v-progress-circular
                  v-else-if="reportLoadingProgress < 100"
                  :size="210"
                  :width="2"
                  color="#51627C"
                  :rotate="-90"
                  :model-value="reportLoadingProgress"
                >
                  {{ reportLoadingMessage }}
                  {{ Math.ceil(reportLoadingProgress) }}%
                </v-progress-circular>
                <v-progress-circular
                  v-else
                  :size="210"
                  :width="2"
                  color="#51627C"
                  indeterminate
                >
                  <div
                    class="text-center"
                    v-html="
                      'Aggregating...' +
                      Math.ceil(aggregationProgressPercentage) +
                      '%,<br>This could take a<br>few minutes.'
                    "
                  />
                </v-progress-circular>
              </v-card-actions>
            </v-card>
            <!--   load in background block       -->
            <v-card
              v-if="reportLoadingDialog"
              elevation="0"
              style="position: relative; top: 20dvh"
            >
              <v-card-actions class="justify-center">
                <div class="text-center loadInBackground">
                  <v-card-text style="font-size: 16px">
                    If you want you can load this report in the background and
                    continue using the site whilst you wait.
                  </v-card-text>
                  <v-btn
                    color="primary"
                    variant="elevated"
                    class="ma-2"
                    tile
                    title="Load Report in Background"
                    @click="backgroundReportGeneration"
                    aria-label="Load"
                  >
                    Load in Background
                  </v-btn>
                </div>
              </v-card-actions>
            </v-card>
            <!-- Main dialog content container -->
            <v-container
              fluid
              class="ma-0 pa-0"
              style="height: 100%; overflow-y: hidden; overflow-x: hidden"
            >
              <v-row
                no-gutters
                style="height: 100%; overflow-y: hidden; overflow-x: hidden"
              >
                <!-- left col: document nav if screen width wide enough -->
                <v-col
                  v-if="displayProps.width >= 1350 && !reportLoadingDialog"
                  :md="2"
                  ref="navCol"
                  class="pa-0 bg-grey-lighten-4"
                  :style="{
                    borderRight: '1px solid rgba(0, 0, 0, 0.12)',
                    height:
                      parseFloat(displayProps.height) - parseFloat(60) + 'px',
                    overflowY: 'auto',
                    overflowX: 'hidden',
                  }"
                >
                  <div
                    :style="{
                      position: 'sticky',
                      top: 0,
                    }"
                  >
                    <v-list
                      bg-color="grey-lighten-4"
                      density="compact"
                      :style="{
                        height:
                          parseFloat(this.displayProps.height) -
                          parseFloat(60) +
                          'px',
                        'overflow-y': 'auto',
                      }"
                    >
                      <v-list-subheader class="ml-2">OUTLINE</v-list-subheader>
                      <v-list-item
                        v-for="(theme, i) in reportThemes"
                        :key="i"
                        style="white-space: normal"
                        variant="plain"
                        color="primary"
                        :active="selectedNav == theme.id"
                        @click="scrollToSection('theme-title-' + theme.id)"
                      >
                        <v-list-item-title
                          class="px-2"
                          style="white-space: normal; font-size: 0.9rem"
                        >
                          {{ theme.title }}
                        </v-list-item-title></v-list-item
                      >
                    </v-list>
                  </div>
                </v-col>
                <!-- middle col: Report content -->
                <v-col
                  @scroll="windowScroll"
                  class="pa-0"
                  :md="
                    displayProps.width >= 1350
                      ? AIPanel
                        ? 7
                        : 10
                      : AIPanel
                        ? 9
                        : 12
                  "
                  :style="{
                    height:
                      parseFloat(displayProps.height) - parseFloat(60) + 'px',
                    overflowY: 'auto',
                    overflowX: 'hidden',
                  }"
                >
                  <div
                    id="reportContent"
                    v-if="!reportLoadingDialog"
                    :style="{
                      width: isFullScreenViewMode
                        ? '100%'
                        : screenWidth >= 1350
                          ? AIPanel
                            ? '100%'
                            : '1250px'
                          : '100%',
                      margin: !isFullScreenViewMode ? '0 auto' : '',
                    }"
                  >
                    <!-- Report Dialog header content-->
                    <v-card-actions class="downloadButton">
                      <v-spacer></v-spacer>
                      <v-tooltip location="bottom" open-delay="500">
                        <template v-slot:activator="{ props }">
                          <v-btn
                            v-bind="props"
                            :disabled="
                              screenModeSwitcherDisabled || screenWidth < 1440
                            "
                            icon="mdi-presentation"
                            size="small"
                            variant="text"
                            :color="headerBtnColour"
                            class="ma-2"
                            @click.prevent="
                              updateReportViewMode(!isFullScreenViewMode)
                            "
                          >
                          </v-btn>
                        </template>
                        <span>{{
                          isFullScreenViewMode
                            ? "Make page fixed-width"
                            : "Make page full width"
                        }}</span>
                      </v-tooltip>
                      <v-tooltip location="bottom">
                        <template v-slot:activator="{ props }">
                          <v-btn
                            v-if="
                              this.$store.state.config.siteConfig
                                .is_public_site &&
                              this.$store.state.isGroupAdmin &&
                              !this.isDemoAccount
                            "
                            v-bind="props"
                            icon="mdi-share-variant"
                            size="small"
                            variant="text"
                            :color="headerBtnColour"
                            class="ma-2"
                            @click="saveReport(1)"
                            :disabled="disablePubButton"
                            :loading="savingPub"
                            aria-label="Use as a Public Report"
                          >
                            <template v-slot:loader>
                              <v-icon light>mdi-cached</v-icon>
                            </template>
                          </v-btn>
                        </template>
                        <span>Use as a Public Report</span>
                      </v-tooltip>
                      <v-tooltip location="bottom">
                        <template v-slot:activator="{ props }">
                          <v-btn
                            v-if="
                              !this.$store.state.config.userProfile
                                .is_public_site_user_profile &&
                              this.reportType !== 'discovery' &&
                              !this.isDemoAccount
                            "
                            v-bind="props"
                            icon="mdi-heart"
                            size="small"
                            variant="text"
                            :color="headerBtnColour"
                            class="ma-2"
                            @click="saveReport()"
                            :disabled="disableFavButton"
                            :loading="savingFav"
                            aria-label="Add to Favourites"
                            data-testid="add-to-favourites-button"
                          >
                            <template v-slot:loader>
                              <v-icon light>mdi-cached</v-icon>
                            </template>
                          </v-btn>
                        </template>
                        <span>add to favourites</span>
                      </v-tooltip>
                      <v-tooltip location="bottom">
                        <template v-slot:activator="{ props }">
                          <v-btn
                            v-bind="props"
                            icon="mdi-download"
                            size="small"
                            variant="text"
                            :color="headerBtnColour"
                            class="ma-2"
                            @click="downloadFullReport()"
                            aria-label="download"
                            :disabled="mapGenerating"
                          >
                          </v-btn>
                        </template>
                        <span>Download Report</span>
                      </v-tooltip>
                      <v-btn
                        v-if="
                          !reportLoadingDialog &&
                          indicatorValues &&
                          Object.keys(indicatorValues).length > 0 &&
                          $store.state.isOcsiMember &&
                          env !== 'prod'
                        "
                        color="info"
                        class="ma-2 white--text"
                        tile
                        title="AI PLayground"
                        @click="AISummaryDialog = !AISummaryDialog"
                        variant="elevated"
                      >
                        AI PLayground
                        <OcsiOnlyChip class="ml-2"></OcsiOnlyChip>
                      </v-btn>
                      <v-btn
                        v-if="!publicSiteUser"
                        color="#F7B348"
                        class="ma-2"
                        tile
                        title="Insights"
                        @click="AIPanel = !AIPanel"
                        variant="elevated"
                        prepend-icon="mdi-creation"
                        style="color: #4d4d4d !important"
                      >
                        Insights
                      </v-btn>
                    </v-card-actions>
                    <!-- Report content -->
                    <v-card-text class="mt-8 flexcard" fluid>
                      <v-row class="ma-0" justify="center">
                        <v-col cols="6">
                          <a href="https://localinsight.org/" target="_blank">
                            <v-img
                              :src="require('@/assets/localInsight.png')"
                              :width="250"
                              class="mr-auto"
                              alt="Site Logo"
                            ></v-img>
                          </a>
                        </v-col>
                        <v-col mb-6 cols="6">
                          <a :href="logoRedirectionURL" target="_blank">
                            <v-img
                              :src="reportLogo"
                              :width="250"
                              class="ml-auto"
                              alt="OCSI Logo"
                            ></v-img>
                          </a>
                        </v-col>
                      </v-row>
                      <v-divider class="mt-10" />
                      <v-row justify="center" class="mt-15" no-gutters>
                        <v-col class="pa-0 d-flex align-center">
                          <v-card-text>
                            <h1
                              class="mb-4"
                              :style="'font-size: ' + titleSize + 'px'"
                            >
                              {{ reportTitle }}
                            </h1>
                            <div
                              v-if="comparatorsList[0]"
                              class="mb-4"
                              :style="
                                'font-size: ' +
                                subtitleSize +
                                'px; line-height: 1.3; color: #51627C; font-weight: 600 !important'
                              "
                              id="customAreaTitleName"
                            >
                              {{ comparatorsList[0].area_name }}
                            </div>
                            <div
                              class="mb-12"
                              :style="`font-size: ${subtitleSize - 4}px; color: #7F7F7F; font-weight: 600 !important`"
                            >
                              Created for
                              {{ config.customClientConfig.client_name }} on
                              {{ date }}
                            </div>
                            <div
                              class="font-weight-regular"
                              style="font-size: 14px; color: #7f7f7f"
                            >
                              <div>
                                Oxford Consultants for Social Inclusion Ltd. All
                                rights reserved. Copyright ©{{
                                  new Date().getFullYear()
                                }}.
                              </div>
                              <div>
                                This report is provided for public benefit use
                                only and must not be used for commercial
                                purposes.
                              </div>
                            </div>
                          </v-card-text>
                        </v-col>
                        <v-col cols="6" class="pa-0">
                          <div
                            v-if="mapGenerating"
                            class="d-flex fill-height justify-center align-center"
                          >
                            <v-progress-circular
                              indeterminate
                              :size="128"
                              :width="2"
                            >
                              <div class="text-center">
                                Generating Map
                              </div></v-progress-circular
                            >
                          </div>
                          <v-img
                            v-if="!isReportManager && !mapGenerating"
                            id="mapImgHTML"
                            width="80%"
                            min-width="300"
                            class="ml-auto"
                            :src="mapImage"
                            alt="Map of Reports Primary Area"
                            @error="
                              mapImage = require('@/assets/mapPlaceholder.png');
                              this.errorMsg({
                                title: 'Unable to generate map image',
                                message:
                                  'The map for this area is too complex to generate and therefore cannot be displayed',
                              });
                            "
                          >
                          </v-img>
                        </v-col>
                      </v-row>
                    </v-card-text>
                    <!--            <v-card-actions class="mt-8">-->
                    <v-card-text class="mt-8">
                      <h1
                        v-if="reportObject && reportObject.introduction"
                        class="mb-7 mt-5"
                      >
                        Introduction
                      </h1>
                      <div
                        v-if="reportObject && reportObject.introduction"
                        v-html="reportObject.introduction"
                        class="min-accessible-size text"
                      />
                      <h1
                        v-if="reportObject && reportObject.site_branding"
                        class="mb-7 mt-5"
                      >
                        Local Insight
                      </h1>
                      <div
                        v-if="reportObject && reportObject.site_branding"
                        v-html="reportObject.site_branding"
                        class="min-accessible-size text"
                      />
                      <h1
                        v-if="reportObject && reportObject.ocsi_branding"
                        class="mb-7 mt-5"
                      >
                        OCSI
                      </h1>
                      <div
                        v-if="reportObject && reportObject.ocsi_branding"
                        v-html="reportObject.ocsi_branding"
                        class="min-accessible-size text"
                      />
                      <h1
                        v-if="reportObject && reportObject.about_ind"
                        class="mb-7 mt-5"
                      >
                        About the indicators
                      </h1>
                      <div
                        v-if="reportObject && reportObject.about_ind"
                        v-html="reportObject.about_ind"
                        class="min-accessible-size text"
                      />
                      <h1
                        v-if="reportObject && reportObject.how_area_identified"
                        class="mb-7 mt-5"
                      >
                        About the area
                      </h1>
                      <div
                        v-if="reportObject && reportObject.how_area_identified"
                        v-html="reportObject.how_area_identified"
                        class="min-accessible-size text"
                      />
                    </v-card-text>
                    <!--            </v-card-actions>-->
                    <div
                      v-for="(theme, i) in reportThemes"
                      :key="i"
                      style="min-width: 600px"
                      :id="'theme-' + theme.id"
                      class="report-theme"
                    >
                      <h2 :id="'theme-title-' + theme.id" class="ml-4">
                        {{ theme.title }}
                      </h2>
                      <v-card-text>
                        <div
                          v-html="theme.description"
                          class="min-accessible-size text"
                        />
                      </v-card-text>
                      <v-card-actions
                        v-for="(item, j) in theme.config"
                        :key="j"
                        style="justify-content: center; align-items: center"
                        class="pa-0"
                      >
                        <!-- Text -->
                        <v-card-text v-if="item.text">
                          <div
                            v-html="item.text"
                            class="min-accessible-size text"
                          />
                        </v-card-text>
                        <!-- KPIs -->
                        <v-card-actions
                          v-if="item.kpi_config"
                          class="px-0"
                          :style="{
                            maxWidth: isFullScreenViewMode
                              ? '100%'
                              : screenWidth > 1400
                                ? '1440px'
                                : '100%',
                            width: '100%',
                          }"
                        >
                          <KPIs :KPIsObject="item.kpi_config"></KPIs>
                        </v-card-actions>
                        <!-- Table -->
                        <v-card-actions
                          v-if="item.indicatorHTMLTableRef"
                          :id="i.toString() + '-' + j.toString()"
                          style="width: 100%"
                          :class="{ 'w-100': isFullScreenViewMode }"
                        >
                          <!-- show skeleton if table not loaded -->
                          <div
                            v-if="
                              !loadItemTriggers[
                                i.toString() + '-' + j.toString()
                              ]
                            "
                            :style="{
                              width: isFullScreenViewMode
                                ? '100%'
                                : screenWidth > 1400
                                  ? '1440px'
                                  : '100%',
                            }"
                          >
                            <v-skeleton-loader
                              class="mx-auto border"
                              type="table-tbody"
                            ></v-skeleton-loader>
                          </div>
                          <!-- show table -->
                          <Tables
                            :load="
                              loadItemTriggers[
                                i.toString() + '-' + j.toString()
                              ]
                            "
                            :item="item"
                            :indicatorValues="indicatorValues"
                            :comparators="selectedComparators"
                            :comparatorsList="comparatorsList"
                            :reportThemeObject="theme"
                          />
                        </v-card-actions>
                        <!-- Chart -->
                        <div
                          v-if="item.chartType"
                          :id="i.toString() + '-' + j.toString()"
                        >
                          <!-- show skeleton if graph not loaded -->
                          <div
                            v-if="
                              !loadItemTriggers[
                                i.toString() + '-' + j.toString()
                              ]
                            "
                          >
                            <v-skeleton-loader
                              class="mx-auto border"
                              height="60vh"
                              type="list-item-two-line, list-item-two-line, list-item-two-line, list-item-two-line, list-item-two-line"
                              style="transform: rotate(-90deg)"
                            ></v-skeleton-loader>
                          </div>
                          <!-- show graph-->
                          <Charts
                            :load="
                              loadItemTriggers[
                                i.toString() + '-' + j.toString()
                              ]
                            "
                            :item="item"
                            :useScreenWidth="screenWidth"
                            :indicatorValues="indicatorValues"
                            :comparators="selectedComparators"
                            :comparatorsList="comparatorsList"
                            :reportThemeObject="theme"
                            :chartWidthProp="chartWidthProp"
                          />
                        </div>
                      </v-card-actions>
                      <v-divider
                        style="margin-top: 50px; margin-bottom: 50px"
                      ></v-divider>
                    </div>
                  </div>
                </v-col>
                <!-- right col: AI panel -->
                <v-col
                  v-if="AIPanel && !reportLoadingDialog"
                  :md="3"
                  :style="{
                    height:
                      parseFloat(displayProps.height) - parseFloat(60) + 'px',
                    overflowY: 'hidden',
                    overflowX: 'hidden',
                  }"
                  class="pa-0"
                >
                  <AIReport
                    :AIPanel="AIPanel"
                    :reportLoadingDialog="reportLoadingDialog"
                    :displayProps="displayProps"
                    :comparatorsList="comparatorsList"
                    :indicatorValues="indicatorValues"
                    :reportType="reportType"
                    :reportTitle="reportTitle"
                    :reportObject="reportObject"
                    :reportThemes="reportThemes"
                    @update:AIPanel="AIPanel = $event"
                /></v-col>
              </v-row>
            </v-container>
            <ReportDocX
              v-model:download="download"
              :content="content"
              :indicatorValues="indicatorValues"
              :comparators="selectedComparators"
              :comparatorsList="comparatorsList"
              :reportMapPrimaryAreaCode="reportMapPrimaryAreaCode"
              :isReportManager="isReportManager"
              :mapImage="mapImage"
            />
          </v-card-text>
        </v-card>

        <!-- AI chat bot -->
        <!-- <AIChatBot
          v-if="$store.state.isOcsiMember && viewReportDialog && env !== 'prod'"
        /> -->
      </v-dialog>

      <!-- AI Report Summary Dialog -->
      <AIReportSummary
        ref="AIReportSummary"
        v-model:AISummaryDialog="AISummaryDialog"
        :comparatorsList="comparatorsList"
        :indicatorValues="indicatorValues"
        :content="content"
        :reportType="reportType"
        :reportTitle="reportTitle"
        :reportObject="reportObject"
        :reportThemes="reportThemes"
      />
    </div>
  </v-theme-provider>
</template>

<script>
import Charts from "@/components/Charts";
import Tables from "@/components/Tables";
import KPIs from "@/components/KPIs";
import AIReport from "@/components/AIReport.vue";
import ReportDocX from "@/components/ReportDocX";
// import AIChatBot from "@/components/AIChatBot";
import OCSILogo from "@/assets/OCSILogo.png";
import AIReportSummary from "./AIReportSummary.vue";
import { systemMessages } from "@/mixins/SystemMessages";
import { useDisplay } from "vuetify";
import { toRaw } from "vue";
import { Debounce } from "@/mixins/Debounce";
import OcsiOnlyChip from "@/components/OcsiOnlyChip";
import { isBrightColour } from "@/mixins/IsBrightColour";

export default {
  name: "REPORTVIEWER",
  data: () => ({
    disablePubButton: true,
    disableFavButton: true,
    savingFav: false,
    savingPub: false,
    loadItemTriggers: {},
    allChartsLoaded: false,
    chartCounter: 0,
    reportObject: null,
    reportLoadingDialog: false,
    reportLoadingMessage: null,
    reportThemes: [],
    indicatorValues: {},
    download: false,
    content: {},
    timer: null,
    importComplete: false,
    progressCheckLimit: 0,
    progressID: null,
    date: null,
    mapGenerating: false,
    mapImage: null,
    screenModeSwitcherDisabled: false,
    reportTitle: null,
    debounceLazyLoad: null,
    debounceWindowResize: null,
    displayProps: null,
    navWidth: 0,
    selectedNav: null,
    AISummaryDialog: false,
    AIPanel: false,
  }),
  components: {
    Charts,
    Tables,
    KPIs,
    ReportDocX,
    // AIChatBot,
    AIReportSummary,
    AIReport,
    OcsiOnlyChip,
  },
  mixins: [systemMessages, Debounce],
  props: {
    isReportManager: {
      type: String,
      default: null,
    },
    /* Dev ENV only  - report sandbox */
    isReportSandbox: {
      type: Boolean,
      default: false,
    },
    /* Dev ENV only  - static report object (sandbox) */
    staticReportObject: {
      type: Object,
      default: () => {},
    },
    /* Dev ENV only  - static report themes (sandbox) */
    staticReportThemes: {
      type: Array,
      default: () => [],
    },
    /* Dev ENV only  - static indicator values (sandbox) */
    staticIndicatorValues: {
      type: Object,
      default: () => {},
    },
    /* Dev ENV only  - static comparators list (sandbox) */
    staticComparatorsList: {
      type: Object,
      default: () => {},
    },
  },
  computed: {
    env() {
      return process.env.VUE_APP_ENVIRONMENT;
    },
    isDemoAccount() {
      return this.$store.state.config.customClientConfig.client_type === "demo"
        ? true
        : false;
    },
    screenWidth() {
      //addition of report navigation - screen is space minus nav
      return this.displayProps.width - this.navWidth;
    },
    chartWidthProp() {
      if (!this.AIPanel) return null;

      // Calculate width based on screen dimensions
      const totalWidth = this.displayProps.width;
      const leftNavWidth =
        this.displayProps.width >= 1350 ? (this.AIPanel ? 2 / 12 : 3 / 12) : 0;
      const aiPanelWidth = this.AIPanel ? 3 / 12 : 0; // AI panel takes 3/12 (25%) of width
      const middleColumnWidth = totalWidth * (1 - leftNavWidth - aiPanelWidth);

      return Math.max(0, middleColumnWidth - 10) + "px";
    },
    saveFileName() {
      return (
        "Local Insight " +
        this.comparatorsList[0].area_name +
        " " +
        this.$store.state.reportType +
        " Report"
      );
    },
    reportLogo() {
      return this.config.customClientConfig.report_logo || OCSILogo;
    },
    logoRedirectionURL() {
      let logo = this.config.customClientConfig.logo_redirection;

      if (logo && !logo.startsWith("https")) {
        return "https://" + logo;
      }

      return logo;
    },
    defaultReportObject: {
      get() {
        return this.$store.state.defaultReportObject;
      },
      set(value) {
        this.$store.commit("setDefaultReportObject", value);
      },
    },
    reportType: {
      get() {
        return this.$store.state.reportType;
      },
      set(value) {
        this.$store.commit("setReportType", value);
      },
    },
    viewReportDialog: {
      get() {
        return this.$store.state.viewReportDialog;
      },
      set(value) {
        this.$store.commit("setViewReportDialog", value);
      },
    },
    reportDownloading: {
      get() {
        return this.$store.state.reportDownloading;
      },
      set(value) {
        this.$store.commit("setReportDownloading", value);
      },
    },
    reportDownloaded: {
      get() {
        return this.$store.state.reportDownloaded;
      },
      set(value) {
        this.$store.commit("setReportDownloaded", value);
      },
    },
    aggregationProgressPercentage: {
      get() {
        return this.$store.state.aggregationProgressPercentage;
      },
      set(value) {
        this.$store.commit("setAggregationProgressPercentage", value);
      },
    },
    reportLoadingProgress: {
      get() {
        return this.$store.state.reportLoadingProgress;
      },
      set(value) {
        this.$store.commit("setReportLoadingProgress", value);
      },
    },
    reportID: {
      get() {
        return this.$store.state.reportID;
      },
      set(value) {
        this.$store.commit("setReportID", value);
      },
    },
    viewReport: {
      get() {
        return this.$store.state.viewReport;
      },
      set(value) {
        this.$store.commit("setViewReport", value);
      },
    },
    comparatorsList: {
      get() {
        return this.$store.state.comparatorsList;
      },
      set(value) {
        this.$store.commit("setComparatorsList", value);
      },
    },
    selectedComparators: {
      get() {
        return this.$store.state.selectedComparators;
      },
      set(value) {
        this.$store.commit("setSelectedComparators", value);
      },
    },
    reportMapPrimaryAreaCode: {
      get() {
        return this.$store.state.reportMapPrimaryAreaCode;
      },
      set(value) {
        this.$store.commit("setReportMapPrimaryAreaCode", value);
      },
    },
    whichCountryThemes: {
      get() {
        return this.$store.state.whichCountryThemes;
      },
      set(value) {
        this.$store.commit("setWhichCountryThemes", value);
      },
    },
    selectedThemes: {
      get() {
        return this.$store.state.selectedThemes;
      },
      set(value) {
        this.$store.commit("setSelectedThemes", value);
      },
    },
    titleSize() {
      if (this.$vuetify.display.xs) {
        return "14";
      }
      if (this.$vuetify.display.sm) {
        return "19";
      }
      if (this.$vuetify.display.md) {
        return "19";
      }
      if (this.$vuetify.display.lg) {
        return "34";
      }
      if (this.$vuetify.display.xl) {
        return "34";
      }
      if (this.$vuetify.display.xxl) {
        return "34";
      }
      return "19";
    },
    subtitleSize() {
      if (this.$vuetify.display.xs) {
        return "14";
      }
      if (this.$vuetify.display.sm) {
        return "14";
      }
      if (this.$vuetify.display.md) {
        return "19";
      }
      if (this.$vuetify.display.lg) {
        return "24";
      }
      if (this.$vuetify.display.xl) {
        return "24";
      }
      if (this.$vuetify.display.xxl) {
        return "24";
      }
      return "19";
    },
    customAreaID() {
      // if the area code hasn't been passed in as a prop, this will be a 'sample' report, so use a known area code
      if (this.reportMapPrimaryAreaCode) {
        return this.reportMapPrimaryAreaCode;
      } else {
        return 146;
      }
    },
    isFullScreenViewMode: {
      get() {
        return this.$store.state.config.userProfile.full_screen_report_view;
      },
      set(value) {
        this.$store.commit("setReportDocumentView", value);
      },
    },
    aiReportSummaryContent: {
      get() {
        return this.$store.state.aiReportSummaryContent;
      },
      set(value) {
        this.$store.commit("setAIReportSummaryContent", value);
      },
    },
    //check whether user is a public site user (not logged in - public site user flag)
    publicSiteUser: {
      get() {
        return (
          this.config.userProfile?.is_public_site_user_profile !== undefined &&
          this.config.userProfile?.is_public_site_user_profile !== false
        );
      },
    },
    headerBtnColour: {
      get() {
        const backgroundColor =
          this.$store.state.config.siteConfig.toolbar_colour;
        return isBrightColour(backgroundColor) ? "black" : "white";
      },
    },
  },
  created() {
    //assign useDisplay composable to variable to use inside functions
    this.displayProps = useDisplay();
  },
  mounted() {
    //get the pixel width of doc navigation column when page loads
    this.$nextTick(() => {
      this.windowResize();
    });
    //define debounced functions
    this.debounceLazyLoad = this.debounce(this.lazyLoad, 50);
    this.debounceWindowResize = this.debounce(this.windowResize, 100);
    this.debounceThemeInView = this.debounce(this.themeInView, 200);

    //add window event listeners
    window.addEventListener("resize", this.debounceWindowResize);

    this.config = this.$store.state.config;

    const options = { year: "numeric", month: "long", day: "numeric" };
    this.date = new Date().toLocaleDateString(undefined, options);

    /**
     * Dev ENV only - if we're looking at the report sandbox then set
     * the static report data (object, indicator values, report themes and
     * comparators) so they don't need to be fetched and aggregated
     * from the backend, and open the report dialog
     */
    if (this.isReportSandbox) {
      if (Object.keys(this.staticReportObject).length > 0) {
        this.reportObject = toRaw(this.staticReportObject);
      }
      if (Object.keys(this.staticIndicatorValues).length > 0) {
        this.indicatorValues = toRaw(this.staticIndicatorValues);
      }
      if (this.staticReportThemes.length > 0) {
        this.reportThemes = toRaw(this.staticReportThemes);
      }
      if (Object.keys(this.staticComparatorsList).length > 0) {
        this.comparatorsList = toRaw(this.staticComparatorsList);
      }
      this.buildChartTriggers();
      this.mapImage = require("@/assets/mapPlaceholder.png");
      this.viewReportDialog = true;
    }
  },
  unmounted() {
    window.removeEventListener("resize", this.debounceWindowResize);
  },
  methods: {
    favPubButtonsController(publicReport = 0) {
      this.disableFavButton = true;
      this.disablePubButton = true;
      var currentReport = {
        public_report: publicReport,
        comparitor_one: this.comparatorsList[0].area_code,
        comparitor_two: this.comparatorsList[1].area_code,
        comparitor_three: this.comparatorsList[2].area_code,
        assigned_themes: this.selectedThemes.map((theme) => theme.themeID),
      };

      this.$axios
        .post("/saved-report-duplication-check", currentReport)
        .then(
          function (response) {
            // handle success
            this.disableFavButton = response.data[0];
            this.disablePubButton = response.data[1];

            // if this isn't a saved report, reset the AI summary content
            if (!this.disableFavButton && !this.disablePubButton) {
              this.aiReportSummaryContent = {};
            }
          }.bind(this),
        )
        .catch(
          function (error) {
            // handle error
            console.error(error);
          }.bind(this),
        );
    },
    buildChartTriggers() {
      this.loadItemTriggers = {};
      this.allChartsLoaded = false;
      for (var i = 0; i < this.reportThemes.length; i++) {
        for (var j = 0; j < this.reportThemes[i].config.length; j++) {
          if (
            this.reportThemes[i].config[j].chartType ||
            this.reportThemes[i].config[j].indicatorHTMLTableRef
          ) {
            this.loadItemTriggers[i.toString() + "-" + j.toString()] = false;
          }
        }
      }
    },
    // add a debouncer
    lazyLoad() {
      for (const key in this.loadItemTriggers) {
        if (this.isInViewport(document.getElementById(key), true)) {
          this.loadItemTriggers[key] = true;
        }
      }
    },
    isInViewport(element, partially = false) {
      if (!element) {
        return false;
      }

      var bounding = element.getBoundingClientRect();

      //element is at least partially in viewport
      const partialCheck =
        (bounding.top > 0 &&
          bounding.top < document.documentElement.clientHeight) ||
        (bounding.bottom > 0 &&
          bounding.bottom < document.documentElement.clientHeight &&
          ((bounding.left > 0 &&
            bounding.left < document.documentElement.clientWidth) ||
            (bounding.right > 0 &&
              bounding.right < document.documentElement.clientWidth)));

      const fullCheck =
        bounding.top >= 0 &&
        bounding.left >= 0 &&
        bounding.right <=
          (window.innerWidth || document.documentElement.clientWidth) &&
        bounding.bottom <=
          (window.innerHeight || document.documentElement.clientHeight);

      if (partially ? partialCheck : fullCheck) {
        return true;
      } else {
        return false;
      }
    },
    backgroundReportGeneration() {
      if (this.reportType === "discovery") {
        this.viewReportDialog = false;
        return;
      } else if (this.$router.currentRoute.path !== "/map") {
        this.$router.push("/map");
      }
      this.viewReportDialog = false;
    },
    saveReport(publicReport = 0) {
      this.savingPub = !!publicReport;
      this.savingFav = !publicReport;
      var savedReport = {
        name: null,
        description: null,
        summary_report: false,
        public_report: publicReport,
        comparitor_one: this.comparatorsList[0].area_code,
        comparitor_two: this.comparatorsList[1].area_code,
        comparitor_three: this.comparatorsList[2].area_code,
        assigned_themes: [],
        ai_output: this.aiReportSummaryContent,
      };

      // custom report assigned themes
      if (this.selectedThemes.length) {
        for (var i = 0; i < this.selectedThemes.length; i++) {
          savedReport.assigned_themes.push(this.selectedThemes[i].themeID);
        }
      }
      this.$axios
        .post("/saved-reports", savedReport)
        .then(
          function (response) {
            console.log(response.data);
            this.$store.commit("setSelectedReportDetails", {
              id: response.data,
            });
            // handle success
            this.disableFavButton = this.disableFavButton
              ? this.disableFavButton
              : !publicReport;
            this.disablePubButton = this.disablePubButton
              ? this.disablePubButton
              : !!publicReport;
            this.savingPub = false;
            this.savingFav = false;
            // let the user know
            this.emit.emit("systemMessage", {
              title: publicReport ? "Public Reports" : "Favourites",
              message: publicReport
                ? "This report is now available to the Public"
                : "This report has been added to your Favourites",
              colour: "success",
              timeout: 3000,
            });
          }.bind(this),
        )
        .catch(
          function (error) {
            this.savingPub = false;
            this.savingFav = false;
            // handle error
            console.error(error);
            this.emit.emit("systemMessage", {
              message: error.response.data.message,
              title: "Error! Failed to Save Report",
              timeout: -1,
              colour: "red",
            });
          }.bind(this),
        );
    },
    getStaticMapURL() {
      /**
       * Report requires a map image or image placeholder in order
       * for docx download not to fail.
       *
       * two known failures during map generation:
       * - dissolving a complex geometry takes too long and times out eventually
       * - the google maps api returns an error e.g. url too long
       *
       * +reasonable timeout added if backend taking too long with failure handling
       * +v-img error handler deals with src error e.g. google maps api
       */
      this.mapGenerating = true;
      this.$axios
        .post("/custom-areas-static-map", [this.customAreaID], {
          timeout: 10000, //timeout call to backend after (ms)
        })
        .then(
          function (response) {
            // handle success
            this.mapImage = response.data;
            this.mapGenerating = false;
          }.bind(this),
        )
        .catch(
          function (error) {
            this.emit.emit("systemBusy", false);
            if (
              error.code === "ECONNABORTED" ||
              (error.response && error.response.status === 413)
            ) {
              //handle timeout error
              this.mapImage = require("@/assets/mapPlaceholder.png");
              /*
              this.errorMsg({
                title: "Unable to generate map image",
                message:
                  "The map for this area is too complex to generate and therefore cannot be displayed",
              });
              */
              this.mapGenerating = false;
            } else {
              // handle other errors
              console.error(error);
              this.emit.emit("systemMessage", {
                message: error.response.data.message,
                title: "Unable to load map",
                timeout: -1,
                colour: "warning",
              });
              this.mapGenerating = false;
            }
          }.bind(this),
        );
    },
    downloadFullReport() {
      this.content = {
        reportTitle:
          this.reportType === "Summary"
            ? this.reportObject.name
            : this.reportObject.custom_report_name,
        description: this.reportObject.description,
        saveFileName: this.saveFileName,
        themes: this.reportThemes,
        reportObject: this.reportObject,
        clientName: this.$store.state.config.customClientConfig.client_name,
        reportLogo: this.$store.state.config.customClientConfig.report_logo,
      };

      this.download = true;
      setTimeout(
        function () {
          for (const key in this.loadItemTriggers) {
            this.loadItemTriggers[key] = true;
          }
        }.bind(this),
        1800,
      );
    },
    setIndicatorValues() {
      this.importComplete = false;
      this.indicatorValues = [];
      this.aggregationProgressPercentage = 0;
      this.progressID = Math.floor(Math.random() * 10000) + 1;
      var tempIndicatorValues = {};
      let indicatorName = null;
      var newKey = null;
      for (var h = 0; h < this.reportThemes.length; h++) {
        for (var i = 0; i < this.reportThemes[h].config.length; i++) {
          for (
            var j = 0;
            j < this.reportThemes[h].config[i].indicators.length;
            j++
          ) {
            // short names and concatinate dates (we add these into the reportThemes here so the docX generation knows what to do)
            this.reportThemes[h].config[i].indicators[j].concatenate_dates =
              this.reportThemes[h].config[i].concatenate_dates;
            this.reportThemes[h].config[i].indicators[
              j
            ].use_short_indicator_name =
              this.reportThemes[h].config[i].use_short_indicator_name;

            for (var k = 0; k < this.comparatorsList.length; k++) {
              // use shortName check
              indicatorName =
                this.reportThemes[h].config[i].use_short_indicator_name &&
                this.reportThemes[h].config[i].indicators[j].shortName !== null
                  ? this.reportThemes[h].config[i].indicators[j].shortName
                  : this.reportThemes[h].config[i].indicators[j].indicatorName;
              newKey =
                this.comparatorsList[k].area_code +
                "." +
                this.reportThemes[h].config[i].indicators[j].indicatorCode +
                "." +
                this.reportThemes[h].config[i].indicators[j].date +
                "." +
                indicatorName;
              if (typeof this.indicatorValues[newKey] === "undefined") {
                this.indicatorValues[newKey] = null;
              }
              tempIndicatorValues[newKey] = this.indicatorValues[newKey];
            }
          }
        }
      }
      tempIndicatorValues.progressID = this.progressID;
      setTimeout(
        function () {
          this.aggregationProgress();
        }.bind(this),
        2000,
      );
      this.$axios
        .post("/indicator-values-by-area", tempIndicatorValues)
        .then(
          function (response) {
            // handle success
            this.indicatorValues = response.data;
            this.reportLoadingDialog = false;
            this.reportDownloaded = true;
            this.reportDownloading = false;
            // demo account stuff
            if (
              this.isDemoAccount &&
              this.reportObject.demoAccountAutoSavedToFavourites
            ) {
              var message = !this.$store.state.demoReportsCount.remaining
                ? "You’ve used all your report generations."
                : "You still have <strong>" +
                  this.$store.state.demoReportsCount.remaining +
                  "</strong> report generations left to use. Enjoy exploring!";
              this.emit.emit("systemMessage", {
                icon: "mdi-check-circle",
                title: "Report Saved to Favourites",
                message:
                  "This report has been added to your favourites, so you can revisit it anytime during your trial without affecting your limit.<br><br>" +
                  message,
                colour: "green-lighten-4",
                textColour: "on-inverted-success",
                timeout: -1,
                showClose: true,
              });
            }
            if (this.isReportManager === null) {
              this.getStaticMapURL();
            }
          }.bind(this),
        )
        .catch(
          function (error) {
            clearTimeout(this.timer);
            this.emit.emit("systemBusy", false);
            // handle error
            console.error(error);
            this.emit.emit("systemMessage", {
              message: error.response.data.message,
              title: "Error! Failed to get Indicator Values",
              timeout: -1,
              colour: "red",
            });
          }.bind(this),
        );
    },
    aggregationProgress() {
      clearTimeout(this.timer);
      if (this.importComplete || this.progressCheckLimit > 9999999999) {
        return;
      }
      // get progress
      this.$axios
        .get("/get-progress/" + this.progressID)
        .then(
          function (response) {
            // handle success
            clearTimeout(this.timer);
            this.progressCheckLimit++;
            this.importComplete = response.data.complete;
            if (response.data.current_total) {
              this.aggregationProgressPercentage = Math.round(
                (response.data.current_total / response.data.expected_total) *
                  100,
              );
            }
            this.timer = setTimeout(
              function () {
                this.aggregationProgress();
              }.bind(this),
              7000,
            );
          }.bind(this),
        )
        .catch(
          function (error) {
            // handle error
            console.error(error);
          }.bind(this),
        );
    },
    loadThemes() {
      this.reportLoadingProgress =
        this.reportThemes.length *
        (100 / this.reportObject.assigned_themes.length);
      if (
        this.reportThemes.length !== this.reportObject.assigned_themes.length
      ) {
        this.getTheme(
          this.reportObject.assigned_themes[this.reportThemes.length].themeID,
        );
      } else {
        this.reportLoadingMessage = "Aggregating Data for Selected Areas...";
        this.setIndicatorValues();
      }
    },
    getTheme(themeID) {
      let i = 0;
      let areadCode = null;
      let url = "/reports-themes/" + themeID;
      while (i < 3) {
        areadCode = null;
        if (typeof this.selectedComparators[i] !== "undefined") {
          areadCode = this.selectedComparators[i];
        }
        url = url + "/" + areadCode;
        i++;
      }
      this.$axios
        .get(url)
        .then(
          function (response) {
            // handle success
            this.reportThemes.push(response.data);
            this.loadThemes();
            this.buildChartTriggers();
          }.bind(this),
        )
        .catch(
          function (error) {
            // handle error
            console.error(error);
            this.emit.emit("systemMessage", {
              message: error.response.data.message,
              title: "Error! Failed to get selected report",
              timeout: -1,
              colour: "red",
            });
          }.bind(this),
        );
    },
    sendAnalytics() {
      this.$axios.put("/send-analytics", {
        event: "reports_usage",
        reportID: this.reportID,
        summary:
          !this.isReportManager && this.selectedThemes.length ? false : true,
        c1: {
          areaName: this.comparatorsList[0].area_name,
          areaCode: this.comparatorsList[0].area_code,
        },
        c2: {
          areaName: this.comparatorsList[1].area_name,
          areaCode: this.comparatorsList[1].area_code,
        },
        c3: {
          areaName: this.comparatorsList[2].area_name,
          areaCode: this.comparatorsList[2].area_code,
        },
        themes: this.selectedThemes,
        moduleName: this.$route.path,
        pageName: this.$route.name,
      });
    },
    getSelectedReport() {
      // send some analytics
      this.sendAnalytics();
      // update config
      this.config = this.$store.state.config;
      this.reportObject = null;
      this.mapImage = null;
      this.reportThemes = [];
      let i = 0;
      let areadCode = null;
      let url = "/reports/" + this.reportID;
      while (i < 3) {
        areadCode = null;
        if (typeof this.comparatorsList[i] !== "undefined") {
          areadCode = this.comparatorsList[i].area_code;
        }
        url = url + "/" + areadCode;
        i++;
      }
      if (this.reportType === "discovery") {
        url = url + "/" + true;
      }

      this.$axios
        .get(url)
        .then(
          function (response) {
            // handle success
            this.reportObject = response.data;
            this.$store.state.demoReportsCount.remaining =
              response.data.demoAccountReportsRemaining;
            this.reportTitle =
              this.reportType === "Summary"
                ? this.reportObject.name
                : this.reportObject.custom_report_name;
            this.whichCountryThemes =
              this.reportObject.which_country_themes.substring(0, 1);

            // if it's custom report then get only selected themes
            if (!this.isReportManager && this.selectedThemes.length) {
              this.reportObject.assigned_themes = this.selectedThemes;
            } else {
              // filter themes by country
              this.reportObject.assigned_themes =
                this.reportObject.assigned_themes.filter((item) => {
                  return item.country === this.whichCountryThemes;
                });
            }
            this.loadThemes();
          }.bind(this),
        )
        .catch(
          function (error) {
            // handle error
            console.error(error);
            this.emit.emit("systemMessage", {
              message: error.response.data.message,
              title: "Error! Failed to get selected report",
              timeout: -1,
              colour: "red",
            });
          }.bind(this),
        );
    },
    initiateReportGeneration() {
      this.favPubButtonsController();
      this.reportDownloading = true;
      this.reportDownloaded = false;
      this.reportLoadingMessage = "Loading Themes...";
      this.reportLoadingProgress = 0;
      this.reportLoadingDialog = true;
      this.viewReportDialog = true;
      this.getSelectedReport();
    },
    checkDemoAccountReportLimit() {
      let currentReport = {
        comparitor_one: this.comparatorsList[0].area_code,
        comparitor_two: this.comparatorsList[1].area_code,
        comparitor_three: this.comparatorsList[2].area_code,
        assigned_themes: this.selectedThemes.map((theme) => theme.themeID),
      };

      this.$axios
        .post("/check-report-limit", currentReport)
        .then(
          function (response) {
            if (response.data.reports_left <= 0) {
              // sorry... no reports left
              this.emit.emit("systemMessage", {
                message: response.data.message,
                title: response.data.header,
                timeout: -1,
                colour: "warning",
              });
            } else {
              // otherwise it's a report from favorites, so just carry on
              this.initiateReportGeneration();
            }
          }.bind(this),
        )
        .catch(
          function (error) {
            // handle error
            console.error(error);
            this.emit.emit("systemMessage", {
              message: error.response.data.message,
              title: "Error! Failed",
              timeout: -1,
              colour: "info",
            });
          }.bind(this),
        );
    },
    updateReportViewMode(value) {
      if (this.screenModeSwitcherDisabled) return;

      this.screenModeSwitcherDisabled = true;
      this.isFullScreenViewMode = value;
      this.$axios
        .post("/set-report-view-mode", {
          full_screen_report_view: value,
        })
        .then()
        .catch(
          function (error) {
            // handle error
            this.isFullScreenViewMode = !this.isFullScreenViewMode;
            console.error(error);
            this.emit.emit("systemMessage", {
              message: error.response.data.message,
              title: "Error! Failed to update report view mode",
              timeout: -1,
              colour: "error",
            });
          }.bind(this),
        )
        .finally(() => {
          setTimeout(() => {
            this.screenModeSwitcherDisabled = false;
          }, 1000);
        });
    },
    scrollToSection(id) {
      this.selectedNav = id;

      const el = document.getElementById(id);
      el.scrollIntoView({ behavior: "smooth" });
    },
    /* updates nav width if screen resizes */
    windowResize() {
      this.navWidth = this.$refs.navCol ? this.$refs.navCol.$el.clientWidth : 0;
    },
    /* event catches window scroll */
    windowScroll(event) {
      //set charts and tables visible when in view
      this.debounceLazyLoad();
      this.debounceThemeInView(event);
    },
    /* determines what report theme is active based on element
     * in centre of screen
     */
    themeInView() {
      //find DOM element in center of screen
      const midScreenElement = document.elementFromPoint(
        this.screenWidth / 2,
        this.displayProps.height / 2,
      );

      //get parent element of class report-theme
      const parentThemeElement = midScreenElement.closest(".report-theme");

      //relevent doc nav to active
      if (parentThemeElement) {
        const themeId = parentThemeElement.getAttribute("id").split("-")[1];
        this.selectedNav = themeId;
      }
    },
  },
  watch: {
    viewReport: {
      handler() {
        if (this.viewReport) {
          // if it's a demo account then check their limit
          if (
            this.$store.state.config.customClientConfig.client_type === "demo"
          ) {
            this.checkDemoAccountReportLimit();
          } else {
            // no limits, just crack on
            this.initiateReportGeneration();
          }
        }
      },
      immediate: true,
    },
    viewReportDialog: {
      handler() {
        if (!this.viewReportDialog) {
          for (const key in this.loadItemTriggers) {
            this.loadItemTriggers[key] = false;
          }
        }
      },
    },
  },
};
</script>

<style scoped>
.downloadButton {
  position: fixed;
  right: 10px;
  top: -5px;
}

.progress {
  top: 10vh;
}

.loadInBackground {
  padding: 20px;
  border-color: #dedbdb;
  border-radius: 0px;
  border-width: 0.1px;
  border-style: solid;
}
.text::v-deep p {
  margin-bottom: 20px !important;
}

.highlight-button {
  border-radius: 10px; /* Ensure the highlight area is squared */
}
</style>

<style>
.highlight-button .v-btn__overlay {
  transition: 0.3s !important; /* make the hover smoother */
}
</style>
